import { Button } from '@1trading/react/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Loading } from '@1trading/react/components/ui/loading';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { useToast } from '@1trading/react/hooks/use-toast';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useCallback } from 'react';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingBackButton, PageHeadingTitle } from '@/components/ui/page-heading';
import { CONTROL_TYPES, ControlType } from '@/constants/institution';
import InstitutionGoToReviewButton from '@/features/institution/components/go-to-review-button';
import { useInstitutionOnboardingBasePath } from '@/features/institution/context/update';
import InstitutionAlgorithm from '@/features/institution/futures/components/algorithm';
import InstitutionFuturesIndividuals from '@/features/institution/futures/components/individuals';
import {
  type InstitutionFuturesExecution,
  type InstitutionFuturesExecutionOrDefaults,
  institutionDetailsExecutionApiToInstitutionFuturesExecution,
  institutionFuturesExecutionSchema,
  institutionFuturesExecutionToInstitutionDetailsExecutionApi,
} from '@/features/institution/futures/execution/schema';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { useInstitutionDetailsMutation, useInstitutionDetailsV2Answers } from '@/features/institution/hooks/useInstitutionDetails';
import { useInstitutionOnboardingNextDetailsRoute } from '@/features/institution/hooks/useInstitutionOnboardingNextDetailsRoute';
import { useInstitutionOnboardingPrevDetailsRoute } from '@/features/institution/hooks/useInstitutionOnboardingPrevDetailsRoute';
import { reportIndividualNationalClientIdentifierFormatErrors } from '@/features/institution/schemas/individual';
import { INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';

const InstitutionFuturesExecutionOnboardingFormForm = ({ defaultValues, onPrevStep }: { defaultValues: InstitutionFuturesExecutionOrDefaults; onPrevStep: () => void }) => {
  const form = useForm<InstitutionFuturesExecution>({
    defaultValues,
    resolver: standardSchemaResolver(institutionFuturesExecutionSchema),
  });
  const { isPending, mutate } = useInstitutionDetailsMutation();
  const onboardingPath = useInstitutionOnboardingBasePath();
  const getNextDetailsRoute = useInstitutionOnboardingNextDetailsRoute('execution');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const execution = form.watch('execution');

  const onSubmit = (data: InstitutionFuturesExecution, { isReturningToReview = false }: { isReturningToReview?: boolean } = {}) => {
    const savedAnswers = { execution: institutionFuturesExecutionToInstitutionDetailsExecutionApi(data) };

    mutate(savedAnswers, {
      onError: () => {
        toast({
          description: t('fundamentals.defaultError.message'),
          variant: 'destructive',
        });
      },
      onSuccess: () => {
        navigate(isReturningToReview ? `${onboardingPath}/review` : getNextDetailsRoute(savedAnswers), { replace: true });
      },
    });
  };

  const onInvalid = () => reportIndividualNationalClientIdentifierFormatErrors(form.getValues('individuals'));

  return (
    <Form {...form}>
      <div className="flex flex-row justify-between -md:justify-end">
        <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={onPrevStep} />
        <InstitutionGoToReviewButton onSubmit={() => form.handleSubmit((data) => onSubmit(data, { isReturningToReview: true }))()} />
      </div>
      <PageCard size="lg" bordered fixedFooter>
        <form name="institution-execution" onSubmit={form.handleSubmit((data) => onSubmit(data), onInvalid)}>
          <PageCardContent className="pt-8 space-y-6">
            <FormField
              name="execution"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('institutionDetails.execution.execution')}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      defaultValue={field.value}
                      variant="cards"
                      aria-label={t('institutionDetails.execution.execution')}
                      onValueChange={(value) => {
                        field.onChange(value);
                        (form as UseFormReturn<InstitutionFuturesExecutionOrDefaults>).setValue(value === ControlType.INDIVIDUAL ? 'algorithm' : 'individuals', undefined);
                      }}
                    >
                      {CONTROL_TYPES.map((answer) => (
                        <FormItem key={answer}>
                          <RadioGroupItemCard variant="outline">
                            <FormControl>
                              <RadioGroupItem value={answer} />
                            </FormControl>
                            <FormLabel contained>{t(`institutionDetails.controlType.options.${answer}`)}</FormLabel>
                          </RadioGroupItemCard>
                        </FormItem>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {{
              [ControlType.ALGORITHM]: <InstitutionAlgorithm />,
              [ControlType.INDIVIDUAL]: <InstitutionFuturesIndividuals section="execution" />,
            }[execution] ?? null}
          </PageCardContent>
          <PageCardFooter>
            <Button className="relative w-full" size="lg" type="submit" variant="primary" disabled={isPending}>
              <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
              {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
            </Button>
          </PageCardFooter>
        </form>
      </PageCard>
    </Form>
  );
};
InstitutionFuturesExecutionOnboardingFormForm.displayName = 'InstitutionFuturesExecutionOnboardingFormForm';

const InstitutionFuturesExecutionOnboardingForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'execution');
  const prevDetailsRoute = useInstitutionOnboardingPrevDetailsRoute('execution');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;

  const handlePrevStep = useCallback(() => {
    navigate(prevDetailsRoute, { replace: true });
  }, [navigate, prevDetailsRoute]);

  if (isLoading) return <LoadingCard />;

  const defaultValues = institutionDetailsAnswers?.execution ? institutionDetailsExecutionApiToInstitutionFuturesExecution(institutionDetailsAnswers.execution) : {};

  return (
    <section className="relative">
      <PageHeading>
        <PageHeadingTitle>{t('institutionDetails.execution.title')}</PageHeadingTitle>
      </PageHeading>
      <InstitutionFuturesExecutionOnboardingFormForm defaultValues={defaultValues} onPrevStep={handlePrevStep} />
    </section>
  );
};
InstitutionFuturesExecutionOnboardingForm.displayName = 'InstitutionFuturesExecutionOnboardingForm';

export default InstitutionFuturesExecutionOnboardingForm;
